import React from 'react'
import { createPortal } from 'react-dom'

import {
  CHART_SEGMENT_GEAR_GAP,
  CHART_SEGMENT_GEAR_INSET,
  CHART_SEGMENT_GEAR_SIZE,
} from './gear-layout'

import type { ChartSegmentAxis } from '@digital-twins/shared-types'
import type { ChartSegmentLabels } from './ChartSegmentContext'

interface ChartSegmentControlProps {
  axes: Array<ChartSegmentAxis>
  selectedOptionId: string
  onSelect: (selection: ChartSegmentSelection) => void
  onOpen: () => void
  /** The Off option's words: the whole sample, or the split a split chart returns to. */
  offLabel: string
  /** The control's chrome, in the viewer's language. */
  labels: ChartSegmentLabels
  /** The axes could not be loaded; offer a retry instead of an empty menu. */
  axesUnavailable?: boolean
  onRetry?: () => void
  /** Distance from the owning panel's top edge. Raised when the panel underlaps
   *  the slide header, so the gear never lands on the eyebrow or client logo. */
  insetTop?: number
}

export interface ChartSegmentSelection {
  id: string
  axisId: string
  levelIds?: Array<string>
}

interface ChartSegmentOption extends ChartSegmentSelection {
  label: string
  /** Number of rows this axis will produce; absent for a single user segment. */
  groupCount?: number
  /** User-created segment rather than a demographic axis. */
  isTag?: boolean
}

/** Never squeeze the menu below this; flip it above the button instead. */
const MIN_MENU_HEIGHT = 220

/**
 * How wide the control ever gets. At rest it is a 44px box; on hover the pill unfurls
 * the words "Break down" leftward, and it is that width — not the resting one — that
 * prints over whatever sits beside it. Both collisions found in review (the
 * matrix-rating scale swatches, the quadrant "Scale Ready" label) were content that
 * cleared the resting box and not the pill. The pill is capped at this width below, so
 * the number is a guarantee rather than an estimate.
 */
export const CHART_SEGMENT_CONTROL_HOVER_WIDTH = 132

/**
 * The pill's own left padding — the gap between the boundary of the reserved region
 * and the first pixel of the pill's ink.
 *
 * The clearance below reserves up to the pill's BOX edge and stops there, where the
 * old resting-box clearance also carried an explicit `CHART_SEGMENT_GEAR_GAP` of
 * breathing room. The gap did not disappear: it moved inside the pill, and it is
 * declared here so the two numbers stay related. Nothing paints in this strip — the
 * pill's background starts at the box edge, but at 7% black over 8-10px of empty
 * padding it reads as the same breathing room, and a reader's eye lands on the
 * label, which is inset by this much.
 */
export const CHART_SEGMENT_CONTROL_PILL_PAD_LEFT = CHART_SEGMENT_GEAR_GAP + 2

/**
 * The control's reserved region, measured leftward from the owning panel's right edge:
 * the corner inset plus the full hovered pill. No slide content may enter it. Use this
 * rather than a per-slide number, so a new chart slide type inherits the clearance.
 *
 * It deliberately does NOT add `CHART_SEGMENT_GEAR_GAP` on top; see
 * `CHART_SEGMENT_CONTROL_PILL_PAD_LEFT`. Every extra pixel here is a pixel taken off
 * the title of seven slides (ChartSegmentTitleSlot), so the region reserves what the
 * pill occupies and no more.
 */
export const CHART_SEGMENT_CONTROL_CLEARANCE_X =
  CHART_SEGMENT_GEAR_INSET + CHART_SEGMENT_CONTROL_HOVER_WIDTH

/**
 * Bottom of that region, measured down from the panel's top edge. Content below this
 * line is clear of the control at any width — that is where the quadrant map's top
 * labels sit.
 */
export function chartSegmentControlClearanceBottom(
  insetTop: number = CHART_SEGMENT_GEAR_INSET,
): number {
  return insetTop + CHART_SEGMENT_GEAR_SIZE
}

function overlappingOptionId(axisId: string, levelId: string): string {
  return JSON.stringify([axisId, levelId])
}

export function ChartSegmentControl(props: ChartSegmentControlProps) {
  const {
    axes,
    selectedOptionId,
    onSelect,
    onOpen,
    offLabel,
    labels,
    insetTop = CHART_SEGMENT_GEAR_INSET,
    axesUnavailable = false,
    onRetry,
  } = props
  const [open, setOpen] = React.useState(false)
  const [hover, setHover] = React.useState(false)
  const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(0)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const didFocusOnOpenRef = React.useRef(false)
  const openFocusTargetRef = React.useRef<'first' | 'last' | 'selected'>('selected')
  const menuId = React.useId()
  const [menuPos, setMenuPos] = React.useState<{
    top?: number
    bottom?: number
    right: number
    maxHeight: number
  } | null>(null)
  // Demographics and user segments are grouped rather than interleaved: a tag can be
  // named "Men", which is indistinguishable from the Gender axis in a flat list.
  // The flat array stays the keyboard order, so it must match the rendered order.
  const offOptions: Array<ChartSegmentOption> = [{ id: 'off', axisId: 'off', label: offLabel }]
  const demographicOptions: Array<ChartSegmentOption> = axes
    .filter((axis) => axis.kind !== 'overlapping')
    .map((axis) => ({
      id: axis.id,
      axisId: axis.id,
      label: axis.label,
      groupCount: axis.levels.length,
    }))
  const segmentOptions: Array<ChartSegmentOption> = axes
    .filter((axis) => axis.kind === 'overlapping')
    .flatMap((axis) =>
      axis.levels.map((level) => ({
        id: overlappingOptionId(axis.id, level.id),
        axisId: axis.id,
        levelIds: [level.id],
        label: level.label || level.id,
        isTag: true,
      })),
    )
  const options: Array<ChartSegmentOption> = [
    ...offOptions,
    ...demographicOptions,
    ...segmentOptions,
  ]
  const demographicsStart = offOptions.length
  const segmentsStart = demographicsStart + demographicOptions.length
  const engaged = hover || open
  const selectedOptionIndex = options.findIndex((option) => option.id === selectedOptionId)
  const isMenuPositioned = menuPos !== null

  function closeMenu(shouldRestoreFocus = true) {
    setOpen(false)
    setHover(false)
    didFocusOnOpenRef.current = false
    if (shouldRestoreFocus) {
      buttonRef.current?.focus()
    }
  }

  function openMenu(focusTarget: 'first' | 'last' | 'selected') {
    openFocusTargetRef.current = focusTarget
    didFocusOnOpenRef.current = false
    setOpen(true)
    setHover(false)
    void onOpen()
  }

  function handleToggle() {
    if (open) {
      closeMenu()
      return
    }
    openMenu('selected')
  }

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      if (open) {
        const nextIndex = event.key === 'ArrowDown' ? 0 : options.length - 1
        setFocusedOptionIndex(nextIndex)
        optionRefs.current[nextIndex]?.focus()
      } else {
        openMenu(event.key === 'ArrowDown' ? 'first' : 'last')
      }
      return
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      event.stopPropagation()
      closeMenu()
    }
  }

  function handleMenuKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Tab') {
      closeMenu()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closeMenu()
      return
    }
    const currentIndex = optionRefs.current.findIndex((option) => option === document.activeElement)
    if (event.key === 'Enter' || event.key === ' ') {
      if (currentIndex < 0 || event.target !== optionRefs.current[currentIndex]) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      optionRefs.current[currentIndex]?.click()
      return
    }
    const handledKeys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!handledKeys.includes(event.key)) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    let nextIndex = currentIndex >= 0 ? currentIndex : Math.max(0, options.findIndex((option) => option.id === selectedOptionId))
    if (event.key === 'ArrowDown') {
      nextIndex = (nextIndex + 1) % options.length
    } else if (event.key === 'ArrowUp') {
      nextIndex = (nextIndex - 1 + options.length) % options.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = options.length - 1
    } else {
      return
    }
    setFocusedOptionIndex(nextIndex)
    optionRefs.current[nextIndex]?.focus()
  }

  React.useEffect(() => {
    if (!open) {
      return
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeMenu()
      }
    }
    function handleMouseDown(event: MouseEvent) {
      const target = event.target as Node
      if (!buttonRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        closeMenu()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [open])

  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuPos(null)
      return
    }
    function update() {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) {
        return
      }
      // The menu is portaled to the body at unscaled size while the deck itself is
      // scaled down, so on a short viewport a long axis list ran straight off the
      // bottom and was cut. Give it only the room that exists, and open upward when
      // there is more of it above.
      const margin = 12
      const below = window.innerHeight - rect.bottom - margin
      const above = rect.top - margin
      const flip = below < MIN_MENU_HEIGHT && above > below
      setMenuPos({
        top: flip ? undefined : rect.bottom + 4,
        bottom: flip ? window.innerHeight - rect.top + 4 : undefined,
        right: window.innerWidth - rect.right,
        maxHeight: Math.max(MIN_MENU_HEIGHT, flip ? above : below),
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  React.useLayoutEffect(() => {
    if (!open || !isMenuPositioned || didFocusOnOpenRef.current || options.length === 0) {
      return
    }
    const nextIndex =
      openFocusTargetRef.current === 'first'
        ? 0
        : openFocusTargetRef.current === 'last'
          ? options.length - 1
          : Math.max(0, selectedOptionIndex)
    const focusedOption = optionRefs.current[nextIndex]
    didFocusOnOpenRef.current = true
    setFocusedOptionIndex(nextIndex)
    focusedOption?.focus()
    focusedOption?.scrollIntoView({ block: 'nearest' })
  }, [isMenuPositioned, open, options.length, selectedOptionIndex])

  const sectionStyle: React.CSSProperties = {
    padding: '10px 10px 4px',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: 'var(--slide-text-muted, #737373)',
    flexShrink: 0,
  }

  function renderOption(option: ChartSegmentOption, index: number) {
    const selected = option.id === selectedOptionId
    return (
      <button
        key={option.id}
        ref={(node) => {
          optionRefs.current[index] = node
        }}
        type="button"
        data-testid="chart-segment-option"
        data-selected={selected ? 'true' : 'false'}
        role="menuitemradio"
        aria-checked={selected}
        tabIndex={focusedOptionIndex === index ? 0 : -1}
        onFocus={() => setFocusedOptionIndex(index)}
        onClick={() => {
          onSelect(option)
          closeMenu()
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'left',
          border: 0,
          background: selected
            ? 'var(--slide-chart-segment-control-selected, var(--slide-neutral-light, #edf2f7))'
            : 'transparent',
          borderRadius: 7,
          padding: '8px 10px',
          fontSize: 13,
          lineHeight: '20px',
          color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
          cursor: 'pointer',
          fontWeight: selected ? 600 : 400,
          flexShrink: 0,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{ flexShrink: 0, opacity: selected ? 1 : 0, color: 'var(--slide-chart-1, #0ebfa5)' }}
        >
          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {option.isTag ? (
          <span
            data-testid="chart-segment-option-dot"
            aria-hidden="true"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--slide-chart-2, #6b3f6b)',
              flexShrink: 0,
            }}
          />
        ) : null}
        <span
          data-testid="chart-segment-option-label"
          style={{
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {option.label}
        </span>
        {option.groupCount ? (
          <span
            data-testid="chart-segment-option-count"
            style={{ fontSize: 11.5, color: 'var(--slide-text-muted, #9AA0A6)', flexShrink: 0 }}
          >
            {labels.groupCount(option.groupCount)}
          </span>
        ) : null}
      </button>
    )
  }

  const popover = open && menuPos ? (
    <div
      ref={menuRef}
      id={menuId}
      data-testid="chart-segment-popover"
      data-chart-segment-control="true"
      role="menu"
      aria-label={labels.breakDownPerSegment}
      onKeyDown={handleMenuKeyDown}
      style={{
        position: 'fixed',
        top: menuPos.top,
        bottom: menuPos.bottom,
        right: menuPos.right,
        maxHeight: menuPos.maxHeight,
        display: 'flex',
        flexDirection: 'column',
        width: 260,
        background: 'var(--slide-chart-segment-control-bg, var(--slide-bg, #ffffff))',
        border: '1px solid var(--slide-chart-segment-control-border, #d7d7d7)',
        borderRadius: 10,
        boxShadow: 'var(--slide-chart-segment-menu-shadow, 0 8px 24px rgb(0 0 0 / 12%))',
        padding: 6,
        zIndex: 10000,
      }}
    >
      <div
        data-testid="chart-segment-popover-header"
        style={{
          padding: '4px 10px 8px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
        }}
      >
        {labels.breakDownBy}
      </div>
      {axesUnavailable ? (
        <div
          data-testid="chart-segment-axes-error"
          style={{
            padding: '4px 10px 10px',
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--slide-text-secondary, #384242)',
            ...(axes.length > 0
              ? {
                  marginBottom: 4,
                  paddingBottom: 8,
                  borderBottom: '1px solid var(--slide-chart-segment-control-border, #e5e7eb)',
                }
              : {}),
          }}
        >
          <div>
            {axes.length > 0
              // A partial failure is the dangerous one: the menu still looks normal,
              // so without this the reader concludes the study has no demographics.
              ? labels.axesPartlyUnavailable
              : labels.axesUnavailable}
          </div>
          <button
            type="button"
            data-testid="chart-segment-axes-retry"
            onClick={() => {
              onRetry?.()
            }}
            style={{
              marginTop: 8,
              minHeight: 32,
              padding: '6px 12px',
              border: '1px solid var(--slide-chart-segment-control-border, #d7d7d7)',
              borderRadius: 8,
              background: 'var(--slide-chart-segment-control-bg, var(--slide-bg, #ffffff))',
              color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {labels.retry}
          </button>
        </div>
      ) : null}
      <div
        data-testid="chart-segment-option-list"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
        }}
      >
        {offOptions.map((option, index) => renderOption(option, index))}
        {offOptions.length > 0 && options.length > offOptions.length ? (
          <div
            data-testid="chart-segment-option-divider"
            style={{
              height: 1,
              margin: '5px 4px',
              background: 'var(--slide-chart-segment-control-border, #e5e7eb)',
              flexShrink: 0,
            }}
          />
        ) : null}
        {demographicOptions.length > 0 ? (
          <div data-testid="chart-segment-section" style={sectionStyle}>
            {labels.demographics}
          </div>
        ) : null}
        {demographicOptions.map((option, index) =>
          renderOption(option, demographicsStart + index),
        )}
        {segmentOptions.length > 0 ? (
          <div data-testid="chart-segment-section" style={sectionStyle}>
            {labels.yourSegments}
          </div>
        ) : null}
        {segmentOptions.map((option, index) => renderOption(option, segmentsStart + index))}
      </div>
    </div>
  ) : null

  return (
    <div
      data-testid="chart-segment-control"
      data-chart-segment-control="true"
      style={{ position: 'absolute', top: insetTop, right: CHART_SEGMENT_GEAR_INSET, zIndex: 4 }}
    >
      <button
        ref={buttonRef}
        type="button"
        data-testid="chart-segment-settings-btn"
        aria-label={labels.breakDownPerSegment}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          // The button keeps a full 44px touch target; the pill inside is the
          // visual, anchored right so its label unfurls leftward without changing
          // the button's box or nudging the chart beneath it.
          position: 'relative',
          width: CHART_SEGMENT_GEAR_SIZE,
          height: CHART_SEGMENT_GEAR_SIZE,
          border: 0,
          padding: 0,
          background: 'transparent',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <span
          data-testid="chart-segment-settings-pill"
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            // Only opacity/transform animate. Animating width and padding relayouts
            // the whole scaled slide on every frame, which is what made the hover
            // feel laggy; the pill is now a constant box that simply fades in.
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            gap: 6,
            height: 30,
            // Caps the pill at the width the reserved region promises, so the region
            // can never be narrower than the thing it is reserving space for.
            maxWidth: CHART_SEGMENT_CONTROL_HOVER_WIDTH,
            overflow: 'hidden',
            borderRadius: 999,
            // Left pad is the breathing room the reserved region no longer adds
            // outside the pill; see CHART_SEGMENT_CONTROL_PILL_PAD_LEFT.
            padding: `0 7px 0 ${CHART_SEGMENT_CONTROL_PILL_PAD_LEFT}px`,
            background: engaged
              ? 'var(--slide-chart-segment-control-hover-bg, rgba(0, 0, 0, 0.07))'
              : 'transparent',
            color: engaged
              ? 'var(--slide-text-primary, #111111)'
              : 'var(--slide-chart-segment-control-fg, var(--slide-text-muted, #9AA0A6))',
            fontSize: 12,
            fontWeight: 600,
            transition: 'color .16s ease, background-color .16s ease',
            // The pill overhangs the 44px button to the left; the button owns the
            // pointer so the overhang never swallows clicks meant for the chart.
            pointerEvents: 'none',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path
              d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h8M16 18h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="2" />
            <circle cx="8" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
            <circle cx="14" cy="18" r="2" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span
            data-testid="chart-segment-settings-label"
            aria-hidden="true"
            style={{
              whiteSpace: 'nowrap',
              opacity: engaged ? 1 : 0,
              transform: engaged ? 'translateX(0)' : 'translateX(8px)',
              transition: 'opacity .16s ease, transform .2s cubic-bezier(.4, 0, .2, 1)',
              willChange: 'opacity, transform',
            }}
          >
            {labels.breakDown}
          </span>
        </span>
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </div>
  )
}
